/**
 * @description Converts a color string (hex, rgb, rgba) to RGB or RGBA format
 * @param colorValue - The color value to convert (hex, rgb, rgba)
 * @param includeAlpha - Whether to include the alpha value in the result
 * @returns The RGB values as a comma-separated string (e.g., "255, 0, 0")
 */
export function extractRgbValues(colorValue: string, includeAlpha = true): string {
  // If it's already in rgb/rgba format, extract the values
  const rgbRegex = /^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/
  const rgbaRegex = /^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/

  let match = colorValue.match(rgbRegex)
  if (match) {
    return `${match[1]}, ${match[2]}, ${match[3]}`
  }

  match = colorValue.match(rgbaRegex)
  if (match) {
    return `${match[1]}, ${match[2]}, ${match[3]}${includeAlpha ? `, ${match[4]}` : ''}`
  }

  // If it's a hex color, convert it to RGB
  const hexRegex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i
  // Short hex format: #RGB or #RGBA
  // eslint-disable-next-line regexp/optimal-quantifier-concatenation
  const shortHexRegex = /^#?([a-f\d])([a-f\d])([a-f\d])([a-f\d])?$/i

  match = colorValue.match(hexRegex)
  if (match) {
    const r = Number.parseInt(match[1], 16)
    const g = Number.parseInt(match[2], 16)
    const b = Number.parseInt(match[3], 16)
    const a = match[4] ? (Number.parseInt(match[4], 16) / 255).toFixed(2) : 1
    return `${r}, ${g}, ${b}${includeAlpha && match[4] ? `, ${a}` : ''}`
  }

  match = colorValue.match(shortHexRegex)
  if (match) {
    const r = Number.parseInt(match[1] + match[1], 16)
    const g = Number.parseInt(match[2] + match[2], 16)
    const b = Number.parseInt(match[3] + match[3], 16)
    const a = match[4] ? (Number.parseInt(match[4] + match[4], 16) / 255).toFixed(2) : 1
    return `${r}, ${g}, ${b}${includeAlpha && match[4] ? `, ${a}` : ''}`
  }

  throw new Error(`Invalid color format: ${colorValue}`)
}
